import { useState } from "react";
import { useShortList } from "@/contexts/short-list-context";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  Bookmark, 
  ChevronLeft, 
  Trash2, 
  Loader2, 
  StickyNote, 
  UserPlus,
  ExternalLink,
  Calendar,
  FileText,
  Handshake
} from "lucide-react";
import { Link } from "wouter";

export function ShortListDrawer() {
  const { 
    items, 
    removeItem, 
    updateNotes,
    isDrawerOpen, 
    closeDrawer, 
    itemCount,
    isGuest,
    isLoading
  } = useShortList();
  const { user } = useAuth();
  const { toast } = useToast();
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState("");

  const handleStartEditNotes = (id: string, currentNotes: string = "") => {
    setEditingNotes(id);
    setNotesValue(currentNotes);
  };

  const handleSaveNotes = async (id: string) => {
    await updateNotes(id, notesValue);
    setEditingNotes(null);
    toast({
      title: "Notes Saved",
      description: "Your notes have been updated.",
    });
  };

  const handleRemove = async (id: string) => {
    await removeItem(id);
    toast({
      title: "Removed",
      description: "Item removed from your short list.",
    });
  };

  const handlePremiumAction = (action: string) => {
    if (isGuest) {
      toast({
        title: "Sign In Required",
        description: `Create a free account to ${action}. Your saved items will be preserved.`,
        action: (
          <Button asChild size="sm" onClick={closeDrawer}>
            <Link href="/login">Sign In</Link>
          </Button>
        ),
      });
      return false;
    }
    return true;
  };

  const getItemNotes = (item: any): string => {
    return item.notes || "";
  };

  const getItemTitle = (item: any): string => {
    return item.title || item.itemTitle || "Untitled";
  };

  const getItemCategory = (item: any): string | undefined => {
    return item.category || item.itemCategory;
  };

  const getItemImage = (item: any): string | undefined => {
    return item.image || item.itemImage;
  };

  return (
    <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && closeDrawer()}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5" />
            My Short List
          </SheetTitle>
          <SheetDescription>
            {isGuest ? (
              <>Save items of interest and sign in to access full features.</>
            ) : (
              <>Your saved items with personal notes and contact options.</>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Button
            variant="ghost"
            className="justify-start gap-2 -ml-2 mb-4"
            onClick={closeDrawer}
            data-testid="button-continue-browsing"
          >
            <ChevronLeft className="h-4 w-4" />
            Continue Browsing
          </Button>

          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {itemCount} {itemCount === 1 ? "Item" : "Items"} saved
              {isGuest && (
                <span className="text-xs block text-muted-foreground/70">
                  (max 5 for guests)
                </span>
              )}
            </div>
            {isGuest && (
              <Button asChild variant="outline" size="sm" onClick={closeDrawer}>
                <Link href="/login" className="gap-1">
                  <UserPlus className="h-3.5 w-3.5" />
                  Sign In
                </Link>
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="flex-1 -mx-6 px-6">
              {items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bookmark className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Your short list is empty</p>
                  <p className="text-sm mt-1">
                    Bookmark products and companies to compare later.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 bg-muted/50 rounded-md space-y-3"
                      data-testid={`short-list-item-${item.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {getItemImage(item) ? (
                          <img
                            src={getItemImage(item)}
                            alt={getItemTitle(item)}
                            className="w-14 h-14 object-cover rounded"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-muted rounded flex items-center justify-center">
                            <Bookmark className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {getItemTitle(item)}
                          </div>
                          {getItemCategory(item) && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              {getItemCategory(item)}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemove(item.id)}
                          data-testid={`button-remove-item-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {editingNotes === item.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={notesValue}
                            onChange={(e) => setNotesValue(e.target.value)}
                            placeholder="Add your notes..."
                            rows={2}
                            className="text-sm"
                            data-testid={`textarea-notes-${item.id}`}
                          />
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleSaveNotes(item.id)}
                              data-testid={`button-save-notes-${item.id}`}
                            >
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setEditingNotes(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {getItemNotes(item) ? (
                            <button
                              onClick={() => handleStartEditNotes(item.id, getItemNotes(item))}
                              className="w-full text-left text-sm text-muted-foreground bg-background/50 p-2 rounded border border-dashed hover-elevate cursor-pointer"
                              data-testid={`button-edit-notes-${item.id}`}
                            >
                              <StickyNote className="h-3 w-3 inline mr-1" />
                              {getItemNotes(item)}
                            </button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-muted-foreground"
                              onClick={() => {
                                if (handlePremiumAction("add personal notes")) {
                                  handleStartEditNotes(item.id, "");
                                }
                              }}
                              data-testid={`button-add-notes-${item.id}`}
                            >
                              <StickyNote className="h-3 w-3 mr-1" />
                              Add notes
                            </Button>
                          )}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (handlePremiumAction("request introductions")) {
                              toast({
                                title: "Coming Soon",
                                description: "Introduction requests will be available soon.",
                              });
                            }
                          }}
                          data-testid={`button-request-intro-${item.id}`}
                        >
                          <Handshake className="h-3.5 w-3.5 mr-1" />
                          Request Intro
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            toast({
                              title: "Coming Soon",
                              description: "Document downloads will be available soon.",
                            });
                          }}
                          data-testid={`button-download-spec-${item.id}`}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          Spec
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (handlePremiumAction("book meetings")) {
                              toast({
                                title: "Coming Soon",
                                description: "Meeting booking will be available soon.",
                              });
                            }
                          }}
                          data-testid={`button-book-meeting-${item.id}`}
                        >
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          Meet
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}

          {isGuest && items.length > 0 && (
            <div className="pt-4 border-t mt-4">
              <div className="text-center text-sm text-muted-foreground mb-3">
                Sign in to unlock full features
              </div>
              <Button asChild className="w-full" onClick={closeDrawer}>
                <Link href="/login">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Free Account
                </Link>
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Your saved items will be preserved
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ShortListButton() {
  const { itemCount, openDrawer, isGuest } = useShortList();

  return (
    <Button
      variant="outline"
      size="sm"
      className="relative gap-2"
      onClick={openDrawer}
      data-testid="button-open-short-list"
    >
      <Bookmark className="h-4 w-4" />
      My List
      {itemCount > 0 && (
        <Badge variant="default" className="ml-1 px-1.5 py-0 text-xs">
          {itemCount}
        </Badge>
      )}
    </Button>
  );
}
